<template>
  <div v-if='cols && cols.length > 0' ref="container">
    <svg xmlns="http://www.w3.org/2000/svg" v-bind:width="currentWidth" v-bind:height="currentHeight"
         v-bind:viewbox="'0 0 ' + currentWidth + ' ' + currentHeight">
      <rect v-bind:x="chartArea.left" v-bind:y="chartArea.top" v-bind:width="chartArea.width"
            v-bind:height="chartArea.height" v-bind:fill="backgroundColor"/>
      <text v-bind:x="chartArea.left" v-bind:y="textArea.topFull" v-bind:font-size="fontSize" font-weight="700"
            fill="#000000">{{ title }}
      </text>
      <g v-for="(y, i) in yAxis" v-bind:key="'y' + i">
        <rect v-bind:x="chartArea.left" v-bind:y="y.coordinate" v-bind:width="chartArea.width" height="1"
              v-bind:fill="y.color"/>
        <text v-if="y.number != null" text-anchor="end" v-bind:x="chartArea.left - (2 * textArea.topHalf)"
              v-bind:y="y.coordinate + textArea.topHalf" v-bind:font-size="fontSize" fill="#444444">
          {{ formatNumber(y.number) }}
        </text>
      </g>
      <g v-for="(x, i) in xAxis" v-bind:key="'x' + i">
        <rect v-bind:x="x.left" v-bind:y="x.top" v-bind:width="x.width" v-bind:height="x.height"
              v-bind:fill="barColor"/>
        <text text-anchor="middle" v-bind:x="x.center" v-bind:y="chartArea.top + chartArea.height + textArea.topFull"
              v-bind:font-size="fontSize" fill="#222222">{{ x.label }}
        </text>
      </g>
    </svg>
  </div>
</template>

<script>
import Vue from 'vue'
export default {
  name: "bar-chart",

  props: {
    cols: {
      type: Array,
      default() {
        return []
      }
    },
    title: {
      type: String,
      default: ""
    },
    width: {
      type: Number,
      default: 480
    },
    height: {
      type: Number,
      default: 240
    },
    fontSize: {
      type: Number,
      default: 14
    },
    marginLeftPercent: {
      type: Number,
      default: 17.5
    },
    marginTopPercent: {
      type: Number,
      default: 12.5
    },
    chartWidthPercent: {
      type: Number,
      default: 82.5
    },
    chartHeightPercent: {
      type: Number,
      default: 75
    },
  },
  data: function () {
    return {
      backgroundColor: "#f5f5f5",
      zeroColor: "#333333",
      gridlineColor: "#dddddd",
      minorGridlineColor: "#eeeeee",
      barColor: "#337ab7",
      currentWidth: 0,
      currentHeight: 0,
      bundle: {
        id_11680: "k",
        id_11681: "m",
        id_11682: "b",
        id_11683: "t",
        id_11684: "q"
      }
    }
  },
  created: function () {
    var instance = this;
    Vue.$bundle("portal", instance.bundle, function (bundle) {
      instance.bundle = bundle;
    });
  },
  computed: {
    textArea: function () {
      return {
        topFull: Math.ceil(this.fontSize * 1.4),
        topHalf: Math.ceil(this.fontSize * 0.4)
      }
    },
    chartArea: function () {
      return {
        left: this.currentWidth * this.marginLeftPercent / 100,
        top: this.currentHeight * this.marginTopPercent / 100,
        width: this.currentWidth * this.chartWidthPercent / 100,
        height: this.currentHeight * this.chartHeightPercent / 100
      };
    },
    minValue: function () {
      var min = null;
      for (var i = 0; i < this.cols.length; i++) {
        var value = this.cols[i][1];
        if (min == null || value < min) {
          min = value != null ? value : 0;
        }
      }
      return min;
    },
    maxValue: function () {
      var max = null;
      for (var i = 0; i < this.cols.length; i++) {
        var value = this.cols[i][1];
        if (max == null || value > max) {
          max = value != null ? value : 0;
        }
      }
      return max;
    },
    range: function () {
      var range;
      if (this.minValue < 0 && this.maxValue < 0) {
        range = -this.minValue;
      } else if (this.minValue > 0 && this.maxValue > 0) {
        range = this.maxValue;
      } else {
        range = this.maxValue - this.minValue;
      }
      return range;
    },
    stepRange: function () {
      var step = this.range / 3;
      if (step > 0) {
        var tens = 0;
        while (step > 10) {
          step = step / 10;
          tens++;
        }
        if (tens % 3 != 0) {
          step = step < 5 ? 5 : 10;
        }
        step = Math.ceil(step) * (Math.pow(10, tens));
        if (this.range / step < 4) {
          step = step / 2;
        }
      }
      return step;
    },
    firstStep: function () {
      if (this.maxValue > 0 && this.stepRange > 0) {
        return Math.ceil(this.maxValue / this.stepRange) * this.stepRange;
      }
      return 0;
    },
    lastStep: function () {
      if (this.minValue < 0 && this.stepRange > 0) {
        return Math.floor(this.minValue / this.stepRange) * this.stepRange;
      }
      return 0;
    },
    numberOfSteps: function () {
      if (this.stepRange > 0) {
        return (this.firstStep - this.lastStep) / this.stepRange;
      }
      return 0;
    },
    stepHeight: function () {
      if (this.numberOfSteps > 0) {
        return this.chartArea.height / (this.numberOfSteps * 2);
      }
      return 0;
    },
    positiveHeight: function () {
      if (this.firstStep > 0 && this.stepRange > 0) {
        return (this.firstStep / this.stepRange) * this.stepHeight * 2;
      }
      return 0;
    },
    negativeHeight: function () {
      if (this.lastStep < 0 && this.stepRange > 0) {
        return (this.lastStep / this.stepRange) * this.stepHeight * 2 * -1;
      }
      return 0;
    },
    xAxis: function () {
      var axis = [];

      var gutters = this.cols.length * 2;
      var gutter = this.chartArea.width / gutters;

      for (var i = 0; i < this.cols.length; i++) {
        var value = this.cols[i][1];
        var height, top;
        if (value > 0) {
          height = value / this.firstStep * this.positiveHeight;
          top = this.chartArea.top + this.positiveHeight - height;
        } else if (value < 0) {
          height = value / this.lastStep * this.negativeHeight;
          top = this.chartArea.top + this.positiveHeight;
        } else if (value == 0) {
          top = this.positiveHeight;
          height = 0;
        } else {
          continue;
        }

        axis.push({
          label: this.cols[i][0],
          center: this.chartArea.left + gutter + (i * gutter * 2),
          left: this.chartArea.left + (i * gutter * 2) + (gutter * 0.6),
          top: top,
          width: gutter * 0.8,
          height: height
        });
      }

      return axis;
    },
    yAxis: function () {
      var axis = [];

      if (this.cols.length > 0) {
        var current = this.firstStep;
        var even = true;

        do {
          axis.push({
            number: even ? current : null,
            coordinate: this.chartArea.top + (axis.length * this.stepHeight),
            color: current == 0 ? this.zeroColor : even ? this.gridlineColor : this.minorGridlineColor
          });
          if (this.stepRange > 0) {
            current -= (this.stepRange / 2);
          } else {
            break;
          }
          even = !even;
        } while (current >= this.lastStep);
      }

      return axis;
    }
  },
  methods: {
    getContainerWidth: function () {
      if ("container" in this.$refs) {
        var element = this.$refs["container"];
        if (typeof element !== "undefined") {
          return element.getBoundingClientRect().width;
        }
      }
      return null;
    },
    formatNumber: function (number) {
      if (number) {
        var negative = number < 0;
        if (negative) {
          number = -number;
        }
        var thousands = 0;
        while (number >= 1000) {
          number = number / 1000;
          thousands++;
        }
        var suffix = "";
        if (thousands >= 5) {
          suffix = " " + this.bundle.id_11684;
        } else if (thousands == 4) {
          suffix = " " + this.bundle.id_11683;
        } else if (thousands == 3) {
          suffix = " " + this.bundle.id_11682;
        } else if (thousands == 2) {
          suffix = " " + this.bundle.id_11681;
        } else if (thousands == 1) {
          suffix = " " + this.bundle.id_11680;
        }
        if (negative) {
          number = -number;
        }
        number = number + suffix;
      }
      return number;
    }
  },
  mounted: function () {
    var containerWidth = this.getContainerWidth();
    this.currentWidth = containerWidth ? containerWidth : this.width;
    this.currentHeight = this.height;
  }
}
</script>
